import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { device } from "../lib/breakpoints";
import Button from "../components/Button";
import { trackEvent } from "../helpers/mixpanel";
import SEO from "../components/SEO";
import { APP_NAME } from "../helpers/constants";

const Clients = () => {
  const [clients, setClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [sortBy, setSortBy] = useState("lastVisit");
  const [selectedClient, setSelectedClient] = useState(null);
  const [showClientModal, setShowClientModal] = useState(false);

  useEffect(() => {
    // Mock client data
    setClients([
      {
        id: 1,
        name: "Sarah Johnson",
        email: "sarah.johnson@email.com",
        phone: "(555) 123-4567",
        joinDate: "2023-12-01",
        lastVisit: "2024-01-14",
        totalSpent: 1250,
        appointmentCount: 3,
        status: "active",
        services: ["Rent collection", "Lease renewal"],
        notes: "On-time payer; asked about pet policy for renewal",
      },
      {
        id: 2,
        name: "Mike Chen",
        email: "mike.chen@email.com",
        phone: "(555) 234-5678",
        joinDate: "2023-11-15",
        lastVisit: "2024-01-13",
        totalSpent: 2100,
        appointmentCount: 7,
        status: "active",
        services: ["Maintenance", "HVAC check-in"],
        notes: "Submitted two repair tickets; both closed",
      },
      {
        id: 3,
        name: "Jessica Martinez",
        email: "jessica.martinez@email.com",
        phone: "(555) 345-6789",
        joinDate: "2023-10-20",
        lastVisit: "2024-01-10",
        totalSpent: 850,
        appointmentCount: 2,
        status: "active",
        services: ["Move-in walkthrough", "Keys"],
        notes: "Move-in complete; no open issues",
      },
      {
        id: 4,
        name: "David Wilson",
        email: "david.wilson@email.com",
        phone: "(555) 456-7890",
        joinDate: "2023-09-10",
        lastVisit: "2023-12-15",
        totalSpent: 300,
        appointmentCount: 1,
        status: "inactive",
        services: ["Unit showing"],
        notes: "Toured; chose another property",
      },
      {
        id: 5,
        name: "Anna Williams",
        email: "anna.williams@email.com",
        phone: "(555) 567-8901",
        joinDate: "2024-01-05",
        lastVisit: "2024-01-12",
        totalSpent: 75,
        appointmentCount: 1,
        status: "new",
        services: ["Inquiry", "Application"],
        notes: "New prospect; background check in progress",
      },
    ]);
  }, []);

  const filteredClients = clients
    .filter((client) => {
      const matchesSearch =
        client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.phone.includes(searchTerm);
      const matchesFilter =
        filterStatus === "all" || client.status === filterStatus;
      return matchesSearch && matchesFilter;
    })
    .sort((a, b) => {
      if (sortBy === "name") return a.name.localeCompare(b.name);
      if (sortBy === "lastVisit")
        return new Date(b.lastVisit) - new Date(a.lastVisit);
      if (sortBy === "totalSpent") return b.totalSpent - a.totalSpent;
      return 0;
    });

  const handleClientClick = (client) => {
    setSelectedClient(client);
    setShowClientModal(true);
    trackEvent("Client Profile Viewed", { client_id: client.id });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "#16a34a";
      case "inactive":
        return "#dc2626";
      case "new":
        return "#2563eb";
      default:
        return "#6b7280";
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Container>
      <SEO
        title={`Client Management - ${APP_NAME} | Owner CRM`}
        description={`Track owner-side contacts, notes, and touchpoints in ${APP_NAME}. (Demo data shown for illustration.)`}
        keywords={`client management, landlord CRM, property contacts, ${APP_NAME}`}
      />

      {/* Header */}
      <Header>
        <HeaderContent>
          <div>
            <PageTitle>Client Management</PageTitle>
            <PageSubtitle>
              Manage your client relationships and appointment history
            </PageSubtitle>
          </div>
          <AddClientButton>
            <ButtonIcon>+</ButtonIcon>
            Add New Client
          </AddClientButton>
        </HeaderContent>
      </Header>

      {/* Filters and Search */}
      <FiltersSection>
        <SearchContainer>
          <SearchInput
            type="text"
            placeholder="Search clients by name, email, or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchContainer>

        <FiltersContainer>
          <FilterGroup>
            <FilterLabel>Status:</FilterLabel>
            <FilterSelect
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="all">All Clients</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="new">New</option>
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Sort by:</FilterLabel>
            <FilterSelect
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="lastVisit">Last Visit</option>
              <option value="name">Name</option>
              <option value="totalSpent">Total Spent</option>
            </FilterSelect>
          </FilterGroup>
        </FiltersContainer>
      </FiltersSection>

      {/* Client Stats */}
      <StatsSection>
        <StatCard>
          <StatValue>{clients.length}</StatValue>
          <StatLabel>Total Clients</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>
            {clients.filter((c) => c.status === "active").length}
          </StatValue>
          <StatLabel>Active Clients</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>
            {clients.filter((c) => c.status === "new").length}
          </StatValue>
          <StatLabel>New Clients</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>
            $
            {clients.reduce((sum, c) => sum + c.totalSpent, 0).toLocaleString()}
          </StatValue>
          <StatLabel>Total Revenue</StatLabel>
        </StatCard>
      </StatsSection>

      {/* Clients List */}
      <ClientsSection>
        {filteredClients.length === 0 ? (
          <EmptyState>
            <EmptyIcon>👥</EmptyIcon>
            <EmptyTitle>No clients found</EmptyTitle>
            <EmptyText>
              {searchTerm || filterStatus !== "all"
                ? "Try adjusting your search or filters"
                : "Start adding clients to manage your customer relationships"}
            </EmptyText>
          </EmptyState>
        ) : (
          <ClientsGrid>
            {filteredClients.map((client) => (
              <ClientCard
                key={client.id}
                onClick={() => handleClientClick(client)}
              >
                <ClientHeader>
                  <ClientInfo>
                    <ClientName>{client.name}</ClientName>
                    <ClientStatus $color={getStatusColor(client.status)}>
                      {client.status}
                    </ClientStatus>
                  </ClientInfo>
                  <ClientValue>${client.totalSpent}</ClientValue>
                </ClientHeader>

                <ClientDetails>
                  <ContactInfo>
                    <ContactItem>
                      <ContactIcon>📧</ContactIcon>
                      <ContactText>{client.email}</ContactText>
                    </ContactItem>
                    <ContactItem>
                      <ContactIcon>📱</ContactIcon>
                      <ContactText>{client.phone}</ContactText>
                    </ContactItem>
                  </ContactInfo>

                  <AppointmentInfo>
                    <InfoItem>
                      <InfoLabel>Last Visit:</InfoLabel>
                      <InfoValue>{formatDate(client.lastVisit)}</InfoValue>
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>Appointments:</InfoLabel>
                      <InfoValue>{client.appointmentCount}</InfoValue>
                    </InfoItem>
                  </AppointmentInfo>

                  <ServicesInfo>
                    <ServicesLabel>Services:</ServicesLabel>
                    <ServicesTags>
                      {client.services.map((service, index) => (
                        <ServiceTag key={index}>{service}</ServiceTag>
                      ))}
                    </ServicesTags>
                  </ServicesInfo>
                </ClientDetails>
              </ClientCard>
            ))}
          </ClientsGrid>
        )}
      </ClientsSection>

      {/* Client Detail Modal */}
      {showClientModal && selectedClient && (
        <ModalOverlay onClick={() => setShowClientModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{selectedClient.name}</ModalTitle>
              <CloseButton onClick={() => setShowClientModal(false)}>
                ×
              </CloseButton>
            </ModalHeader>

            <ModalBody>
              <ClientDetailSection>
                <SectionTitle>Contact Information</SectionTitle>
                <DetailGrid>
                  <DetailItem>
                    <DetailLabel>Email:</DetailLabel>
                    <DetailValue>{selectedClient.email}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Phone:</DetailLabel>
                    <DetailValue>{selectedClient.phone}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Join Date:</DetailLabel>
                    <DetailValue>
                      {formatDate(selectedClient.joinDate)}
                    </DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Status:</DetailLabel>
                    <ClientStatus
                      $color={getStatusColor(selectedClient.status)}
                    >
                      {selectedClient.status}
                    </ClientStatus>
                  </DetailItem>
                </DetailGrid>
              </ClientDetailSection>

              <ClientDetailSection>
                <SectionTitle>Appointment Summary</SectionTitle>
                <DetailGrid>
                  <DetailItem>
                    <DetailLabel>Total Appointments:</DetailLabel>
                    <DetailValue>{selectedClient.appointmentCount}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Total Spent:</DetailLabel>
                    <DetailValue>${selectedClient.totalSpent}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Last Visit:</DetailLabel>
                    <DetailValue>
                      {formatDate(selectedClient.lastVisit)}
                    </DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Services:</DetailLabel>
                    <ServicesTags>
                      {selectedClient.services.map((service, index) => (
                        <ServiceTag key={index}>{service}</ServiceTag>
                      ))}
                    </ServicesTags>
                  </DetailItem>
                </DetailGrid>
              </ClientDetailSection>

              <ClientDetailSection>
                <SectionTitle>Notes</SectionTitle>
                <NotesText>{selectedClient.notes}</NotesText>
              </ClientDetailSection>
            </ModalBody>

            <ModalFooter>
              <SecondaryButton onClick={() => setShowClientModal(false)}>
                Close
              </SecondaryButton>
              <PrimaryButton>Schedule Appointment</PrimaryButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default Clients;

// Styled Components
const Container = styled.div`
  width: 100%;
  background: var(--color-bg-primary);
  padding: 2rem;
  min-height: 100vh;

  @media ${device.mobile} {
    padding: 1rem;
  }
`;

const Header = styled.header`
  margin-bottom: 2rem;
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  @media ${device.mobile} {
    flex-direction: column;
    gap: 1rem;
  }
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: 0.5rem;
  font-family: "Inter", sans-serif;

  @media ${device.mobile} {
    font-size: 1.75rem;
  }
`;

const PageSubtitle = styled.p`
  font-size: 1.1rem;
  color: var(--color-text-secondary);
  font-family: "Inter", sans-serif;
  margin: 0;
`;

const AddClientButton = styled(Button)`
  background: var(--gradient-primary);
  color: var(--color-text-light);
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 8px;
  border: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    transform: translateY(-1px);
    box-shadow: var(--shadow-medium);
  }
`;

const ButtonIcon = styled.span`
  font-size: 1.2rem;
  font-weight: bold;
`;

const FiltersSection = styled.section`
  background: var(--color-bg-secondary);
  padding: 1.5rem;
  border-radius: 12px;
  border: 1px solid var(--color-border-light);
  margin-bottom: 2rem;
  box-shadow: var(--shadow-light);

  @media ${device.mobile} {
    padding: 1rem;
  }
`;

const SearchContainer = styled.div`
  margin-bottom: 1rem;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.875rem;
  border: 1px solid var(--color-border-medium);
  border-radius: 8px;
  font-size: 1rem;
  font-family: "Inter", sans-serif;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px rgba(60, 141, 188, 0.1);
  }
`;

const FiltersContainer = styled.div`
  display: flex;
  gap: 2rem;

  @media ${device.mobile} {
    flex-direction: column;
    gap: 1rem;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const FilterLabel = styled.label`
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--color-text-primary);
  font-family: "Inter", sans-serif;
`;

const FilterSelect = styled.select`
  padding: 0.5rem;
  border: 1px solid var(--color-border-medium);
  border-radius: 6px;
  font-size: 0.9rem;
  font-family: "Inter", sans-serif;
  background: white;

  &:focus {
    outline: none;
    border-color: var(--color-primary);
  }
`;

const StatsSection = styled.section`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media ${device.mobile} {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }
`;

const StatCard = styled.div`
  background: var(--color-bg-secondary);
  padding: 1.5rem;
  border-radius: 12px;
  border: 1px solid var(--color-border-light);
  box-shadow: var(--shadow-light);
  text-align: center;

  @media ${device.mobile} {
    padding: 1rem;
  }
`;

const StatValue = styled.div`
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--color-primary);
  margin-bottom: 0.25rem;
  font-family: "Inter", sans-serif;

  @media ${device.mobile} {
    font-size: 1.5rem;
  }
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  color: var(--color-text-secondary);
  font-family: "Inter", sans-serif;
  font-weight: 500;
`;

const ClientsSection = styled.section``;

const ClientsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1.5rem;

  @media ${device.mobile} {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const ClientCard = styled.div`
  background: var(--color-bg-secondary);
  border-radius: 12px;
  border: 1px solid var(--color-border-light);
  box-shadow: var(--shadow-light);
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-medium);
  }
`;

const ClientHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const ClientInfo = styled.div`
  flex: 1;
`;

const ClientName = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: 0.25rem;
  font-family: "Inter", sans-serif;
`;

const ClientStatus = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: capitalize;
  font-family: "Inter", sans-serif;
  background: ${(props) => `${props.$color}20`};
  color: ${(props) => props.$color};
`;

const ClientValue = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--color-primary);
  font-family: "Inter", sans-serif;
`;

const ClientDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ContactIcon = styled.span`
  font-size: 0.8rem;
`;

const ContactText = styled.span`
  font-size: 0.9rem;
  color: var(--color-text-secondary);
  font-family: "Inter", sans-serif;
`;

const AppointmentInfo = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const InfoLabel = styled.span`
  font-size: 0.8rem;
  color: var(--color-text-muted);
  font-family: "Inter", sans-serif;
  font-weight: 500;
`;

const InfoValue = styled.span`
  font-size: 0.9rem;
  color: var(--color-text-secondary);
  font-family: "Inter", sans-serif;
`;

const ServicesInfo = styled.div``;

const ServicesLabel = styled.div`
  font-size: 0.8rem;
  color: var(--color-text-muted);
  margin-bottom: 0.5rem;
  font-family: "Inter", sans-serif;
  font-weight: 500;
`;

const ServicesTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
`;

const ServiceTag = styled.span`
  padding: 0.25rem 0.5rem;
  background: var(--color-accent-light);
  color: var(--color-primary);
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 500;
  font-family: "Inter", sans-serif;
`;

const EmptyState = styled.div`
  padding: 4rem 2rem;
  text-align: center;
  background: var(--color-bg-secondary);
  border-radius: 12px;
  border: 1px solid var(--color-border-light);
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.5;
`;

const EmptyTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 500;
  color: var(--color-text-secondary);
  margin-bottom: 0.5rem;
  font-family: "Inter", sans-serif;
`;

const EmptyText = styled.p`
  font-size: 1rem;
  color: var(--color-text-muted);
  font-family: "Inter", sans-serif;
  margin: 0;
`;

// Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 2rem;
`;

const ModalContent = styled.div`
  background: var(--color-bg-primary);
  border-radius: 16px;
  max-width: 600px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: var(--shadow-dark);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid var(--color-border-light);
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--color-text-primary);
  font-family: "Inter", sans-serif;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 2rem;
  color: var(--color-text-secondary);
  cursor: pointer;
  padding: 0;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: var(--color-text-primary);
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
`;

const ClientDetailSection = styled.div`
  margin-bottom: 2rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: 1rem;
  font-family: "Inter", sans-serif;
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const DetailLabel = styled.span`
  font-size: 0.9rem;
  color: var(--color-text-muted);
  font-family: "Inter", sans-serif;
  font-weight: 500;
`;

const DetailValue = styled.span`
  font-size: 1rem;
  color: var(--color-text-primary);
  font-family: "Inter", sans-serif;
`;

const NotesText = styled.p`
  font-size: 1rem;
  color: var(--color-text-secondary);
  font-family: "Inter", sans-serif;
  line-height: 1.6;
  margin: 0;
  padding: 1rem;
  background: var(--color-bg-secondary);
  border-radius: 8px;
  border: 1px solid var(--color-border-light);
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1.5rem;
  border-top: 1px solid var(--color-border-light);
`;

const PrimaryButton = styled(Button)`
  background: var(--gradient-primary);
  color: var(--color-text-light);
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 8px;
  border: none;
`;

const SecondaryButton = styled(Button)`
  background: var(--color-bg-secondary);
  color: var(--color-text-primary);
  border: 1px solid var(--color-border-medium);
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 8px;
`;
